// Network discriminator — the rest of the app uses this to pick an SDK
// config, RPC client, or wallet org.
//
//   - `local`    — engineer dev stack. Set `_LOCAL` env vars in `.env.local`
//                  (api/url/programId) to point the SDK at a
//                  localhost backend while still using real Solana devnet
//                  RPC + program IDs. Available only when those env vars
//                  are populated; production Vercel deployments never set
//                  `_LOCAL` so this network silently disappears there.
//   - `devnet`   — Solana devnet. The "demo mode" surface in production.
//   - `mainnet`  — Solana mainnet. Real money.
//
// All three are first-class runtime networks. The user-facing toggle in
// the header flips between `devnet` and `mainnet`; the dev toolkit can
// also flip into `local` when its env vars are present.

export type Network = 'local' | 'devnet' | 'mainnet';

export const NETWORKS: readonly Network[] = ['local', 'devnet', 'mainnet'] as const;

// ALLOW-UNKNOWN: type guard accepts arbitrary input to narrow into a Network.
export function isNetwork(value: unknown): value is Network {
  return value === 'local' || value === 'devnet' || value === 'mainnet';
}

/** Subset of `Network` exposed by the production header toggle —
 *  `local` is dev-only and never surfaces on Vercel. */
export type UserVisibleNetwork = 'devnet' | 'mainnet';

// ALLOW-UNKNOWN: type guard accepts arbitrary input to narrow into UserVisibleNetwork.
export function isUserVisibleNetwork(value: unknown): value is UserVisibleNetwork {
  return value === 'devnet' || value === 'mainnet';
}

/** Alias kept for callsites that still use the old name. Same shape
 *  as `isNetwork` — accepts all three. */
export const isToggleableNetwork: typeof isNetwork = isNetwork;
